import React, { useCallback, useEffect, useState } from 'react';
import { Modal, ModalBody, ModalHeader } from '@/components/common';
import { LoginForm } from '@/components/auth/LoginForm';
import { RegisterForm, type RegisterFormValues } from '@/components/auth/RegisterForm';
import { useAppContext } from '@/hooks/useAppContext';
import { ApiRequestError, acceptInvitation, previewInvitation } from '@/services/invitation-api';
import type { LoginCredentials } from '@/types/auth.types';
import type { InvitationPreview } from '@shared/types.js';
import { RoomRole } from '@shared/types.js';
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaSpinner,
  FaWater
} from 'react-icons/fa';

/** `player` is a "Resident" everywhere a person can read it (§4.1). */
const ROLE_LABELS: Record<string, string> = {
  [RoomRole.ADMIN]: 'Admin',
  [RoomRole.PLAYER]: 'Resident',
  [RoomRole.GUEST]: 'Guest'
};

const DEAD_LINK = 'This invitation is no longer valid';
const MISMATCH = 'This invitation was sent to a different email address';

interface InviteLandingProps {
  token: string;
  /** Called once the invitation is redeemed, so the app can leave this view. */
  onAccepted: () => void;
}

type Stage = 'loading' | 'form' | 'accepted' | 'dead';

/**
 * The `/invite/<token>` screen. It shows what the invitation offers, then does
 * one of three things:
 *
 * - already signed in → accept straight away;
 * - the address has an account → log in, then accept;
 * - it has no account → register, which creates the account with the invited
 *   address and redeems the invitation in the same request.
 *
 * Errors are deliberately incurious: every dead link reads the same, and a
 * mismatch never names the invited address (§3.1 invariant 4).
 */
export const InviteLanding: React.FC<InviteLandingProps> = ({ token, onAccepted }) => {
  const { auth } = useAppContext();
  const { isAuthenticated, login, registerWithInvitation, clearError } = auth;

  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [stage, setStage] = useState<Stage>('loading');
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  // Set when registration finds an account already using the invited address.
  const [mustLogIn, setMustLogIn] = useState(false);

  const redeem = useCallback(async () => {
    setIsBusy(true);
    try {
      await acceptInvitation(token);
      setStage('accepted');
      setError(null);
    } catch (err) {
      if (err instanceof ApiRequestError && err.code === 'invitation_email_mismatch') {
        // Stay on the form: another account may be the right one.
        setError(MISMATCH);
      } else if (err instanceof ApiRequestError && err.code === 'invitation_invalid') {
        setStage('dead');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to accept the invitation');
      }
    } finally {
      setIsBusy(false);
    }
  }, [token]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const loaded = await previewInvitation(token);
        if (cancelled) return;
        setPreview(loaded);
        setStage('form');

        // Already signed in: nothing to ask, just redeem it.
        if (isAuthenticated) {
          await redeem();
        }
      } catch {
        if (!cancelled) setStage('dead');
      }
    })();

    return () => {
      cancelled = true;
    };
    // Deliberately keyed on the token alone: signing in mid-flow must not
    // re-run the preview and re-accept.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleLogin = useCallback(
    async (credentials: LoginCredentials) => {
      setError(null);
      clearError();
      try {
        await login(credentials);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Login failed');
        return;
      }
      await redeem();
    },
    [login, clearError, redeem]
  );

  const handleRegister = useCallback(
    async (values: RegisterFormValues) => {
      setError(null);
      clearError();
      try {
        await registerWithInvitation({ token, ...values });
        setStage('accepted');
      } catch (err) {
        if (err instanceof ApiRequestError && err.code === 'account_exists') {
          // The address already has an account; the link is redeemed by
          // logging in and accepting instead.
          setMustLogIn(true);
          setError('An account already exists for this address. Sign in to accept the invitation.');
          return;
        }
        if (err instanceof ApiRequestError && err.code === 'invitation_invalid') {
          setStage('dead');
          return;
        }
        setError(err instanceof Error ? err.message : 'Registration failed');
      }
    },
    [registerWithInvitation, clearError, token]
  );

  const showLogin = preview?.emailKnown || mustLogIn || isAuthenticated;

  return (
    <Modal
      isOpen={true}
      onClose={() => {}}
      size="xl"
      showCloseButton={false}
      className="bg-gradient-to-b from-game-dark/95 via-game-dark/90 to-game-medium/30 backdrop-blur-md"
    >
      <ModalHeader showCloseButton={false}>
        <div className="text-center w-full">
          <div className="flex items-center justify-center mb-4">
            <img
              src="/assets/logo/minka-logo-color.png"
              alt="Minka"
              className="h-20 w-auto object-contain"
            />
          </div>
          <h2 className="text-xl font-bold text-white">You have been invited</h2>
        </div>
      </ModalHeader>

      <ModalBody>
        {stage === 'loading' && (
          <div className="flex items-center justify-center gap-3 py-10 text-gray-200">
            <FaSpinner className="animate-spin text-game-golden" />
            <span>Loading your invitation…</span>
          </div>
        )}

        {stage === 'dead' && (
          <div className="p-4 bg-game-orange/20 border border-game-orange/50 text-game-accent rounded-lg">
            <div className="flex items-center gap-3">
              <FaExclamationTriangle className="text-game-golden flex-shrink-0" />
              <span className="font-medium">{DEAD_LINK}</span>
            </div>
            <p className="mt-2 text-sm text-gray-200">
              Ask an island admin to send you a new invitation.
            </p>
          </div>
        )}

        {stage === 'accepted' && (
          <div className="space-y-6 text-center">
            <div className="flex items-center justify-center gap-3 text-green-300">
              <FaCheckCircle />
              <span className="font-medium">
                {preview?.roomName
                  ? `You now have access to ${preview.roomName}.`
                  : 'Your invitation has been accepted.'}
              </span>
            </div>
            <button
              type="button"
              onClick={onAccepted}
              className="px-6 py-3 rounded-lg bg-gradient-to-r from-game-golden to-game-orange text-game-dark font-semibold"
            >
              Continue to Minka
            </button>
          </div>
        )}

        {stage === 'form' && preview && (
          <div className="space-y-6">
            <InvitationSummaryCard preview={preview} />

            {error && (
              <div className="p-4 bg-game-orange/20 border border-game-orange/50 text-game-accent rounded-lg">
                <div className="flex items-center gap-3">
                  <FaExclamationTriangle className="text-game-golden flex-shrink-0" />
                  <span className="font-medium">{error}</span>
                </div>
              </div>
            )}

            {showLogin ? (
              <>
                <p className="text-sm text-gray-200">
                  Sign in with the account for the invited address to accept.
                </p>
                <LoginForm onSubmit={handleLogin} isLoading={isBusy || auth.isLoading} error={null} />
              </>
            ) : (
              <RegisterForm onSubmit={handleRegister} isLoading={auth.isLoading} error={null} />
            )}
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

/** What the invitation offers: island, organization, role, inviter, expiry. */
const InvitationSummaryCard: React.FC<{ preview: InvitationPreview }> = ({ preview }) => {
  const expires = new Date(preview.expiresAt);

  return (
    <div className="p-4 bg-game-medium/20 border border-game-medium/50 rounded-lg space-y-2">
      <div className="flex items-center gap-2 text-white font-semibold">
        <FaWater className="text-game-golden" />
        <span>{preview.roomName ?? 'Minka'}</span>
        {preview.organizationName && (
          <span className="text-sm font-normal text-gray-300">· {preview.organizationName}</span>
        )}
      </div>
      <dl className="text-sm text-gray-200 space-y-1">
        <div className="flex gap-2">
          <dt className="text-gray-400">Invited by</dt>
          <dd>{preview.invitedByUsername}</dd>
        </div>
        {preview.role && (
          <div className="flex gap-2">
            <dt className="text-gray-400">Role</dt>
            <dd>
              {ROLE_LABELS[preview.role] ?? preview.role}
              {preview.guestDays ? ` · ${preview.guestDays} days of access` : ''}
            </dd>
          </div>
        )}
        <div className="flex gap-2">
          <dt className="text-gray-400">Link expires</dt>
          <dd>{expires.toLocaleDateString()}</dd>
        </div>
      </dl>
    </div>
  );
};
